import './Truncate.sass';

import React, { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { QuillDeltaToHtmlConverter } from 'quill-delta-to-html';

import { TruncateProps } from '$shared/components/Truncate/Truncate.interface';
import { formatApiUrl, isTrustedDomain, rnbr } from '$utils';
import { findLink, findLinkQuill } from '$shared/components/Linkify/Linkify';
import { Parser, ProcessNodeDefinitions } from 'html-to-react';
import { Link } from 'react-router5';
import { FEED, RESOLVE } from '$shared/constants/pages';

const segmenter = typeof Intl !== 'undefined' && Intl.Segmenter
  ? new Intl.Segmenter()
  : null;

const graphemeLength = (str: string): number => {
  if (!segmenter) return str.length;
  let count = 0;
  for (const _ of segmenter.segment(str)) count++;
  return count;
};

const graphemeSlice = (str: string, end: number): string => {
  if (!segmenter) return str.slice(0, end);
  let result = '';
  let i = 0;
  for (const { segment } of segmenter.segment(str)) {
    if (i >= end) break;
    result += segment;
    i++;
  }
  return result;
};

const cutText = (
  children: ReactNode,
  limit = 200,
): {
  children: ReactNode;
  length: number;
} => {
  const result = React.Children.map(children, (child) => {
    if (limit === 0) {
      return null;
    } else if (typeof child === 'string') {
      const len = graphemeLength(child);
      if (limit < len) {
        const temp = limit;
        limit = 0;
        return graphemeSlice(child, temp).trimRight() + '...';
      } else {
        limit = limit - len;
        return child;
      }
    } else if (limit > 0 && React.isValidElement(child)) {
      const { children, length } = cutText(child.props.children, limit);
      limit = length;
      return React.cloneElement(child, {
        children: children,
      });
    }
  });
  return {
    children: result,
    length: limit,
  };
};

const Truncate: FC<TruncateProps> = (props) => {
  const {
    text,
    cut,
    lines = 0,
    findLinks = true,
    expandText,
    onExpand,
    limit = 0,
    adId = 0,
  } = props;

  const textParsed = useMemo(() => {
    if (text && text.substring && text.substring(0, 1) === '{') {
      try {
        const ops = JSON.parse(text).ops;
        let opsRes: any[] = [];
        for (let i = 0; i < ops.length; i++) {
          ops[i].insert = ops[i].insert.replace(/\n{3,}/g, '\n\n');
          const links = findLinks
            ? findLinkQuill(ops[i].insert, {
                adId,
              })
            : null;
          if (links) {
            for (let item of links) {
              if (item.type === 'text') {
                opsRes.push({
                  type: 'text',
                  insert: item.text,
                  attributes: ops[i].attributes,
                });
              } else {
                const trusted = isTrustedDomain(item.url);
                opsRes.push({
                  type: 'link',
                  insert: item.text,
                  attributes: {
                    ...ops[i].attributes,
                    link: trusted
                      ? item.url
                      : formatApiUrl('/away', {
                          url: item.url,
                          adId: adId || 0,
                        }),
                    target: trusted ? '_self' : '_blank',
                    text: item.text,
                    schema: item.schema,
                  },
                });
              }
            }
          } else if (ops[i].attributes?.link) {
            const trusted = isTrustedDomain(ops[i].attributes.link);
            opsRes.push({
              ...ops[i],
              type: 'link',
              attributes: {
                ...ops[i].attributes,
                text: ops[i].insert,
                ...(trusted
                  ? { target: '_self' }
                  : {
                      link: formatApiUrl('/away', {
                        url: ops[i].attributes.link,
                        adId: adId || 0,
                      }),
                      target: '_blank',
                    }),
              },
            });
          } else {
            opsRes.push(ops[i]);
          }
        }
        return opsRes;
      } catch (e) {}
    }

    return text;
  }, [text]);

  const plainText = useMemo(() => {
    if (typeof textParsed === 'string') {
      return textParsed;
    } else {
      let result = '';
      for (let item of textParsed) {
        result += item.insert;
      }
      return result;
    }
  }, [textParsed]);

  const [isTextMinimized, setTextMinimized] = useState(
    limit &&
      (plainText.length - 20 > limit || plainText.split('\n').length > lines) &&
      !cut,
  );

  useEffect(() => {
    !limit && setTextMinimized(false);
  }, [limit, isTextMinimized]);

  const content = useMemo(() => {
    if (typeof textParsed !== 'string') {
      try {
        let ops = [...textParsed];

        if (isTextMinimized || cut) {
          const opsFiltered: any[] = [];
          let len = 0;
          for (let item of ops) {
            const copy = { ...item };
            const availLen = (!cut ? limit : 92) - len;
            if (availLen <= 0) {
              break;
            }

            const insertLen = graphemeLength(copy.insert);
            if (insertLen < availLen) {
              opsFiltered.push(copy);
            } else {
              copy.insert = graphemeSlice(copy.insert, availLen) + '...';
              opsFiltered.push(copy);
              break;
            }

            len += insertLen;
          }

          ops = [...opsFiltered];
        }

        const converter = new QuillDeltaToHtmlConverter(ops, {
          paragraphTag: 'span',
          encodeHtml: true,
          classPrefix: 'ql',
          inlineStyles: false,
          multiLineBlockquote: false,
          multiLineHeader: false,
          multiLineCodeblock: false,
          multiLineParagraph: true,
          // linkRel: '',
          linkTarget: '_blank',
          allowBackgroundClasses: false,
          // urlSanitizer: (url: string): string | undefined => {
          //   return url;
          // },
          customTagAttributes: (elementData) => {
            if ('link' in elementData.attributes) {
              const { schema, text, onClick } = elementData.attributes;
              return {
                schema,
                text,
                onclick: onClick,
              };
            }
          },
        });

        const html = converter.convert();
        const htmlParser = new Parser(React);
        const processNodeDefinitions = new ProcessNodeDefinitions(React);

        const processingInstructions = [
          {
            shouldProcessNode: (node) => {
              return node.name === 'a' && node.attribs.schema === 'hashtag';
            },
            processNode: (node) => {
              return (
                <Link
                  routeName={FEED}
                  routeParams={{
                    section: 'search',
                    query: node.attribs.text,
                  }}
                >
                  {node.attribs.text}
                </Link>
              );
            },
          },
          {
            shouldProcessNode: (node) => {
              return node.name === 'a' && node.attribs.schema === 'mention';
            },
            processNode: (node) => {
              return (
                <Link
                  routeName={RESOLVE}
                  routeParams={{ domain: node.attribs.text.replace(/^@/, '') }}
                >
                  {node.attribs.text}
                </Link>
              );
            },
          },
          {
            shouldProcessNode: () => true,
            processNode: processNodeDefinitions.processDefaultNode,
          },
        ];

        return htmlParser.parseWithInstructions(html, () => true, processingInstructions);
      } catch (e) {}
    }

    const formattedText = text.replace(/\n{3,}/g, '\n\n');
    const preparedText = !cut ? formattedText : formattedText.substring(0, 92) + '...';

    const content = findLinks
      ? findLink(
          isTextMinimized
            ? React.Children.map(rnbr(preparedText), (child, n) =>
                n < lines ? child : null,
              )
            : rnbr(preparedText),
        )
      : rnbr(preparedText);
    return isTextMinimized ? cutText(content, limit).children : content;
  }, [text, textParsed, limit, lines, isTextMinimized, cut]);

  const handleExpandText = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      e.preventDefault();
      onExpand && onExpand();
      setTextMinimized(false);
    },
    [onExpand],
  );

  if (!plainText.trim()) {
    return null;
  }

  return (
    <div className="Truncate">
      {content}
      {!!isTextMinimized && (
        <>
          &nbsp;&nbsp;
          <div onClick={handleExpandText} className="Truncate__text__expand">
            {expandText}
          </div>
        </>
      )}
    </div>
  );
};

export default Truncate;
